import type { PerformancePart } from '@tunasong/schemas'
import { normalizeParts, performancePartLabels, performanceParts } from '@tunasong/schemas'
import { Checkbox, DebouncedTextField, FormControlLabel, FormGroup, Typography } from '@tunasong/ui-lib'
import type { ReactElement } from 'react'
import { useId } from 'react'
import { bandParts } from './parts.js'
import type { PerformanceParts } from './parts.js'
import styles from './stage-setup-editor.module.css'

export interface PartsStepProps<P extends PerformanceParts> {
  /** The band dialog edits this band's set; without it (Profile) the default set is edited */
  band?: { id: string; name: string }
  performance?: P
  otherInstruments?: string
  onPerformanceChange(next: P): void
  onOtherChange(value: string | undefined): void
}

/**
 * Toggle a part in the profile default. The default may be emptied (no default means a band asks with the picker), and
 * Full chart is exclusive in both directions. Moved here from the retired default-parts checklist.
 */
const toggleDefaultPart = (current: readonly PerformancePart[], part: PerformancePart) => {
  if (part === 'full') {
    return current.includes('full') ? undefined : (['full'] satisfies PerformancePart[])
  }
  const others = current.filter(p => p !== 'full')
  return normalizeParts(others.includes(part) ? others.filter(p => p !== part) : [...others, part])
}

const toggleBandPart = (current: readonly PerformancePart[], part: PerformancePart) =>
  normalizeParts(current.includes(part) ? current.filter(p => p !== part) : [...current, part])

/**
 * "What do you play?": the performance parts, the same vocabulary Perform uses, plus Other for instruments outside
 * them. In Profile it is the default set (and keeps Full chart, which Perform relies on); in a band it is that band's.
 */
export const PartsStep = <P extends PerformanceParts>(props: PartsStepProps<P>): ReactElement => {
  const { band, performance, otherInstruments, onPerformanceChange, onOtherChange } = props
  const legendId = useId()
  const helperId = useId()

  const options = band ? performanceParts.filter(part => part !== 'full') : performanceParts
  const selected = band ? bandParts(performance, band.id) : (normalizeParts(performance?.defaultParts) ?? [])

  const handleToggle = (part: PerformancePart) => () => {
    if (!band) {
      onPerformanceChange({ ...performance, defaultParts: toggleDefaultPart(selected, part) } as P)
      return
    }
    const { [band.id]: _previous, ...otherBands } = performance?.partsByBand ?? {}
    const next = toggleBandPart(selected, part)
    onPerformanceChange({ ...performance, partsByBand: next ? { ...otherBands, [band.id]: next } : otherBands } as P)
  }

  return (
    <section className={styles.section} aria-labelledby={legendId}>
      <Typography id={legendId} variant="h6" component="h3" className={styles.sectionTitle}>
        What do you play?
      </Typography>
      {band ? null : (
        <Typography id={helperId} variant="body2" color="textSecondary" className={styles.description}>
          Used for new bands and your own songs. Each band, and each song, can override them.
        </Typography>
      )}
      <FormGroup role="group" aria-labelledby={legendId} aria-describedby={band ? undefined : helperId}>
        {options.map(part => (
          <FormControlLabel
            key={part}
            control={<Checkbox checked={selected.includes(part)} onCheckedChange={handleToggle(part)} />}
            label={performancePartLabels[part]}
          />
        ))}
      </FormGroup>
      <DebouncedTextField
        className={styles.field}
        label="Other"
        placeholder="Horns, percussion"
        value={otherInstruments ?? ''}
        onDebouncedChange={value => onOtherChange(value.trim() || undefined)}
      />
    </section>
  )
}

export default PartsStep
